import { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';

import { useAuth } from '../../hooks/auth';
import useDebounce from '../../hooks/useDebounce';
import api from '../../services/api';
import { getBankName } from '../../services/brasilapi';
import Input from '../../components/Input';
import InputSelect from '../../components/InputSelect';
import Button from '../../components/Button';
import Notification from '../../components/Notification';
import ProfileUserImage from '../../components/ProfileUserImage';
import ConfirmModal from '../../components/ConfirmModal';
import DocumentFields from '../../components/DocumentFields';

import { Container, InputContainer, ButtonsContainer } from './styles';

const accountTypes = [
  { value: '', label: 'Nenhum' },
  {
    label: 'Conta-corrente',
    value: 'Conta-corrente',
  },
  {
    label: 'Conta-poupança',
    value: 'Conta-poupança',
  },
];

const UserProfile = () => {
  const { userType, signOut } = useAuth();
  const formRef = useRef(null);
  const [bankName, setBankName] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [userImage, setUserImage] = useState(null);
  const [isLoading, setLoading] = useState(true);
  // isLoading cobre a busca do perfil na montagem; salvar tem estado próprio.
  const [isSubmitting, setSubmitting] = useState(false);
  const [windowWidth, setWindoWidth] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  // Guarda o perfil como veio do servidor para saber o que de fato mudou.
  const [currentEmail, setCurrentEmail] = useState('');
  const pendingSubmit = useRef(null);
  // Só visual: nasce ligado quando o perfil veio sem CPF.
  const [isForeigner, setForeigner] = useState(false);

  const debouncedBankCode = useDebounce(bankCode, 500);

  useEffect(() => {
    function handleResize() {
      setWindoWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (debouncedBankCode) {
      getBankName(debouncedBankCode).then(value => setBankName(value));
    }
  }, [debouncedBankCode]);

  async function setAccountTypeField(value) {
    formRef.current.setFieldValue('accountType', { value, label: value });
  }

  useEffect(() => {
    async function setFormData(data) {
      const formData = data;
      if (data.birthDate) {
        const [date] = data.birthDate.split('T');
        formData.birthDate = date;
      }
      if (data.bankCode) {
        setBankCode(data.bankCode);
      }

      if (!formRef.current) return;
      formRef.current.setData(formData);
      setAccountTypeField(formData.accountType);
      setUserImage(data.image);
      setCurrentEmail(data.email || '');
      setForeigner(!data.cpf);
    }

    (async () => {
      try {
        const { data } = await api.get('/user');
        setFormData(data);
      } catch (err) {
        Notification({
          message: 'Erro ao buscar dados do usuário',
          type: Notification.types.ERROR,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function cleanPasswordsFields() {
    formRef.current.setFieldValue('oldPassword', '');
    formRef.current.setFieldValue('password', '');
    formRef.current.setFieldValue('confirmPassword', '');
  }

  async function saveProfile(form) {
    formRef.current.setErrors({});

    // Um documento ou o outro: o que não vale para esta escolha vai nulo, para
    // o back apagar o anterior em vez de guardar os dois.
    const data = {
      ...form,
      ...(isForeigner
        ? { cpf: null }
        : { passportNumber: null, passportCountry: null }),
    };

    try {
      setSubmitting(true);
      await api.put('/user', data);
      Notification({
        message: 'Perfil atualizado com sucesso',
        type: Notification.types.SUCCESS,
      });
      cleanPasswordsFields();
      setCurrentEmail(data.email || currentEmail);
    } catch (err) {
      Notification({
        message: err.response.data.error,
        type: Notification.types.ERROR,
      });

      const { errors } = err.response.data;
      if (errors) {
        formRef.current.setErrors(err.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(data) {
    const emailChanged = Boolean(data.email) && data.email !== currentEmail;

    if (emailChanged) {
      pendingSubmit.current = data;
      setShowEmailChangeModal(true);
      return;
    }

    await saveProfile(data);
  }

  function handleEmailChangeCancel() {
    pendingSubmit.current = null;
    setShowEmailChangeModal(false);
  }

  async function handleEmailChangeConfirm() {
    const data = pendingSubmit.current;
    pendingSubmit.current = null;
    setShowEmailChangeModal(false);

    if (data) await saveProfile(data);
  }

  async function handleBankCodeChange(e) {
    setBankCode(e.target.value);
  }

  async function handleDeleteAccount() {
    setShowDeleteModal(true);
    try {
      await api.delete('/user');
      await signOut();
      Notification({
        message: 'Conta excluída com sucesso',
        type: Notification.types.SUCCESS,
      });
      window.location.href = '/';
    } catch (err) {
      Notification({
        message: err.response.data.error,
        type: Notification.types.ERROR,
      });
    }
  }

  const isDesigner = userType === 'designer';

  return (
    <>
      {isLoading && <div className="loader" />}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir minha conta"
        html={
          '<p>Você tem certeza que deseja excluir sua conta? Todos os seus créditos e projetos serão perdidos. <strong>ESSA AÇÃO NÃO PODE SER DESFEITA.</strong></p> <br><p>A Due Laser ainda manterá seus dados pessoais, mas não será possível acessar sua conta e será necessário realizar um novo cadastro.</p> <br><p>Caso queira remover seus dados pessoais, entre em contato com o suporte pelo email <a href="mailto:suporte@duelaser.com">suporte@duelaser.com</a></p><br>'
        }
        onConfirm={handleDeleteAccount}
      />
      <ConfirmModal
        isOpen={showEmailChangeModal}
        onClose={handleEmailChangeCancel}
        title="Confirmar troca de e-mail"
        html={
          '<p>Você está trocando o e-mail da sua conta. Ao confirmar, <strong>sua conta deixa de ser verificada</strong> e enviaremos um novo link de confirmação para o endereço informado.</p><br><p>Você só conseguirá entrar de novo depois de abrir esse link. Confira se o endereço está correto e se você tem acesso a ele.</p><br>'
        }
        onConfirm={handleEmailChangeConfirm}
      />
      <Container>
        <h1>Meus dados</h1>
        <ProfileUserImage userImage={userImage} />
        <Form ref={formRef} onSubmit={handleSubmit}>
          <p>Dados pessoais</p>
          <Input name="name" label="Nome" />
          <Input name="email" label="E-mail" />
          <DocumentFields isForeigner={isForeigner} onToggle={setForeigner} />
          <Input
            type="date"
            name="birthDate"
            label="Data de nascimento"
            width={windowWidth > 800 ? 35 : null}
          />
          <Input name="description" label="Descrição" multiline />
          <p>Senha</p>
          <Input name="oldPassword" label="Senha antiga" type="password" />
          <Input name="password" label="Nova senha" type="password" />
          <Input
            name="confirmPassword"
            label="Confirmar senha"
            type="password"
          />
          {isDesigner && (
            <>
              <p>Dados bancários</p>
              <Input name="recipientName" label="Nome do destinatário" />
              <InputContainer>
                <Input
                  type="number"
                  name="bankCode"
                  label="Código do banco"
                  width={windowWidth > 800 ? 45 : 100}
                  onChange={handleBankCodeChange}
                />
                <Input
                  name="bankName"
                  label="Nome do banco"
                  disabled
                  value={bankName}
                />
                <Input
                  name="operation"
                  label="Operação (Se aplicável)"
                  width={windowWidth > 800 ? 60 : 100}
                />
              </InputContainer>
              <InputContainer>
                <Input
                  name="agency"
                  label="Agência"
                  width={windowWidth > 800 ? 50 : 100}
                />
                <Input
                  name="account"
                  label="Conta"
                  width={windowWidth > 800 ? 50 : 100}
                />
                <InputSelect
                  name="accountType"
                  label="Tipo de conta"
                  options={accountTypes}
                  width={windowWidth > 800 ? 50 : 100}
                />
              </InputContainer>
            </>
          )}
        </Form>
        <ButtonsContainer>
          {isDesigner ? (
            <div />
          ) : (
            <Button
              text="Excluir minha conta"
              danger
              onClick={() => setShowDeleteModal(true)}
            />
          )}

          <Button
            text="Salvar dados"
            loadingText="Salvando..."
            isLoading={isSubmitting}
            primary
            onClick={() => formRef.current.submitForm()}
          />
        </ButtonsContainer>
      </Container>
    </>
  );
};

export default UserProfile;
